/* eslint-disable camelcase */
import React, { Component } from 'react'
import { StyleSheet, css } from 'aphrodite'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  CircularProgress,
  Typography,
  Tooltip,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { FilterHelper } from '@slx/slixta-common'

import { TableComponent } from '../../../common/components/TableComponent'
import { AppButton } from '../../../common/components/AppButton'
import { DynamicFormDialog } from '../../../common/components/DynamicFormDialog'
import { ApiHelper } from '../../../common/helpers/ApiHelper'
import { Helper } from '../../../common/helpers/Helper'
import { UiHelper } from '../../../common/helpers/UiHelper'
import { AppConfig } from '../../../AppConfig'
import { AppStyles, AppTheme } from '../../../styles/AppTheme'
import { CommonTheme } from '../../../styles/Styles'
import { AppHelper } from '../../../helpers/AppHelper'
import { CdpHelper } from '../../../helpers/CdpHelper'
import { CRMHelper } from '../../../helpers/CRMHelper'

const SETTINGS_KEY = 'CRM_CUSTOM_STAT_CARDS'
const VALUE_KEY = 'ltv'
const CONTACTS_KEY = 'contacts'
const FILTER_FIELD_KEYS = ['state', 'tags', 'tags!']

function isUnfilteredContactsCard(card) {
  return Boolean(
    card
    && getCardType(card) === 'FILTER'
    && (!card.filter || String(card.filter).trim() === '')
  )
}

function parseStatCards(rawString) {
  const rows = []
  String(rawString != null ? rawString : '')
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((segment, index) => {
      const row = { key: '', label: '', filter: '' }
      segment.split(';').forEach((part) => {
        const trimmed = part.trim()
        if (!trimmed) return
        const colonIdx = trimmed.indexOf(':')
        if (colonIdx === -1) return
        const fieldKey = trimmed.slice(0, colonIdx).trim()
        const rawValue = trimmed.slice(colonIdx + 1).trim()
        if (fieldKey === 'key' || fieldKey === 'label' || fieldKey === 'filter') {
          row[fieldKey] = rawValue
        }
      })
      if (!row.key && !row.label) return
      rows.push({
        ...row,
        id: row.key || `card_${index}`,
        order: rows.length + 1,
      })
    })
  return rows
}

function serializeStatCards(cards) {
  return (cards || [])
    .map((card) => {
      const pairs = []
      if (card.key) pairs.push(`key:${card.key}`)
      if (card.label) pairs.push(`label:${card.label}`)
      if (card.filter) pairs.push(`filter:${card.filter}`)
      return pairs.join('; ')
    })
    .filter(Boolean)
    .join(' | ')
}

function getCardType(card) {
  if (card && card.key && String(card.key) === VALUE_KEY) {
    return 'VALUE'
  }
  return 'FILTER'
}

function getCardTypeLabel(cardType) {
  return cardType === 'VALUE' ? 'CONTACTS VALUE' : 'CONTACTS COUNT'
}

function toCamelCaseKey(label) {
  const words = String(label || '')
    .trim()
    .replace(/[^a-zA-Z0-9\s]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
  if (words.length === 0) return 'statCard'
  return words
    .map((word, index) => {
      const lower = word.toLowerCase()
      if (index === 0) return lower
      return lower.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join('')
}

function generateUniqueKey(label, existingKeys, reservedKeys = []) {
  const base = toCamelCaseKey(label) || 'statCard'
  const taken = new Set([...(existingKeys || []), ...(reservedKeys || [])].filter(Boolean))
  if (!taken.has(base)) return base
  let suffix = 2
  while (taken.has(`${base}${suffix}`)) {
    suffix += 1
  }
  return `${base}${suffix}`
}

function buildFilterFormDefConf(fieldDef) {
  const showInternal = Helper.isShowInternal()
  return {
    fields: {
      tags: (() => {
        const fieldData = {}
        const tagsField = fieldDef ? Helper.getField(fieldDef, 'tags') : null
        if (tagsField) {
          fieldData.operators = [{ key: 'any', label: 'any' }, { key: 'all', label: 'all' }]
        }
        if (tagsField && tagsField.options && !showInternal) {
          fieldData.options = tagsField.options.filter((option) => option.key !== 'INTERNAL')
        }
        return fieldData
      })(),
      'tags!': (() => {
        const fieldData = { operators: null, label: 'Tags (Excluded)' }
        const tagsField = fieldDef ? Helper.getField(fieldDef, 'tags') : null
        if (tagsField && tagsField.options && !showInternal) {
          fieldData.options = tagsField.options.filter((option) => option.key !== 'INTERNAL')
        }
        return fieldData
      })(),
    },
    includedFilters: FILTER_FIELD_KEYS,
  }
}

function resolveOptionLabel(filter, val) {
  const option = filter && Array.isArray(filter.options)
    ? filter.options.find((item) => item && item.key === val)
    : null
  if (option && (option.label || option.text)) {
    return option.label || option.text
  }
  return val
}

function buildChipLabel(chipLabel, key, value) {
  let keyPart
  if (key.endsWith('*')) {
    keyPart = <span>{chipLabel + ' contains '} </span>
  } else if (key.endsWith('!')) {
    keyPart = <span>{chipLabel + ' is not '} </span>
  } else {
    keyPart = <span>{chipLabel + ' is '}</span>
  }

  const valuePart = Array.isArray(value) ? (
    <span>
      {value.map((item, index) => (
        <span key={index}>
          <span className={css(Styles.chipTextValue)}>{item}</span>
          {index < value.length - 1 && (key.endsWith('~') ? ' and ' : ' or ')}
        </span>
      ))}
    </span>
  ) : (
    <span className={css(Styles.chipTextValue)}>{value}</span>
  )

  return <span className={css(Styles.chipContainer)}>{keyPart}{valuePart}</span>
}

function StatCardFilterChips({ filter, filterOptions }) {
  if (!filter || !filterOptions || filterOptions.length === 0) {
    return <span className={css(Styles.emptyFilter)}>—</span>
  }

  let searchFilters = {}
  try {
    searchFilters = FilterHelper.getFilterFormData(
      filterOptions,
      `https://filter.slixta.com?${filter}`,
      Helper.getLocalTimezone()
    ) || {}
  } catch (err) {
    return <span className={css(Styles.rawFilter)}>{filter}</span>
  }

  const entries = Object.entries(searchFilters).filter(([key, value]) => {
    if (!key || key.startsWith('_')) return false
    if (value == null) return false
    if (Array.isArray(value) && value.length === 0) return false
    return true
  })

  if (entries.length === 0) {
    return <span className={css(Styles.emptyFilter)}>—</span>
  }

  return (
    <div className={css(Styles.chipsRow)}>
      {entries.map(([key, value]) => {
        const filterField = filterOptions.find((item) => item.key === key)
          || filterOptions.find((item) => item.key === FilterHelper.getFilterBaseKey(key))
        const chipLabel = FilterHelper.getChipLabelForFilterKey(key, filterOptions)
        let filterValue = value
        if (filterField && filterField.options) {
          if (Array.isArray(value)) {
            filterValue = value.map((val) => resolveOptionLabel(filterField, val)).filter(Boolean)
          } else {
            filterValue = resolveOptionLabel(filterField, value)
          }
        }
        return (
          <Chip
            key={key}
            label={buildChipLabel(chipLabel, key, filterValue)}
            size='medium'
            classes={{ root: css(Styles.chipRoot) }}
          />
        )
      })}
    </div>
  )
}

export class LeadFunnelDialog extends Component {
  constructor(props) {
    super(props)
    this.state = {
      cards: [],
      contactFieldDef: null,
      filterOptions: [],
      loading: false,
      saving: false,
      errMsg: '',
      modalOpen: false,
      formDefinition: null,
      editingCard: null,
      currentOrderChangeIndex: null,
      orderChangeEvent: null,
      fetchState: ApiHelper.State.READY,
      isOrderChanged: false,
      orderUIMode: ApiHelper.ORDER_UI_MODE.INTERACTIVE,
    }
  }

  componentDidMount() {
    if (this.props.open) {
      this.bootstrap()
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.bootstrap()
    }
  }

  bootstrap = async () => {
    this.setState({
      loading: true,
      errMsg: '',
      cards: this.loadCardsFromSettings(),
      modalOpen: false,
      formDefinition: null,
      editingCard: null,
    })
    try {
      const contactFieldDef = await this.fetchContactFieldDef()
      const filterOptions = this.buildFilterOptions(contactFieldDef)
      this.setState({
        contactFieldDef,
        filterOptions,
        loading: false,
      })
    } catch (err) {
      this.setState({
        loading: false,
        errMsg: Helper.getErrorMsg(err) || 'Failed to load funnel settings',
      })
    }
  }

  loadCardsFromSettings = () => {
    const raw = AppHelper.getSetting(this.props.orgId, SETTINGS_KEY) || ''
    return parseStatCards(raw)
  }

  fetchContactFieldDef = async () => {
    const [response, crmContactAttrs] = await Promise.all([
      ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['crmcontacts'],
        queryParams: {
          orgId: this.props.orgId,
          pb: 'fieldDef',
          includeCustomFields: 'true',
          limit: 1,
        },
      }),
      this.fetchContactAttrs(),
    ])
    const fieldDef = (response && response.fieldDef) || null
    if (fieldDef && crmContactAttrs) {
      // Tag options live in the contact attrs catalog, not in the fieldDef
      Helper.loadAttrOptions(fieldDef, crmContactAttrs, 'tags')
      const tagsField = Helper.getField(fieldDef, 'tags')
      if (tagsField && tagsField.options) {
        tagsField.options.sort((a, b) => String(a.label).localeCompare(String(b.label)))
      }
    }
    return fieldDef
  }

  fetchContactAttrs = async () => {
    const groupId = 'settingscrmcontactattrs' + this.props.orgId
    try {
      return await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['items', `${groupId}-all`],
        queryParams: { orgId: this.props.orgId, groupId },
      })
    } catch (err) {
      return null
    }
  }

  buildFilterOptions = (fieldDef) => {
    if (!fieldDef) return []
    const filterFormDefConf = buildFilterFormDefConf(fieldDef)
    const { filterOptions } = CRMHelper.getFilterOptions(fieldDef, filterFormDefConf, 'https://filter.slixta.com')
    const stateField = filterOptions.find((f) => f && f.key === 'state')
    if (stateField) {
      UiHelper.renameLeadStatusFieldOptions(stateField, this.props.orgId)
      UiHelper.removeHiddenCrmContactStates(stateField, this.props.orgId)
    }
    return filterOptions || []
  }

  persistCards = async (nextCards) => {
    const orgId = this.props.orgId
    const previousValue = AppHelper.getSetting(orgId, SETTINGS_KEY) || ''
    const settingsValue = serializeStatCards(nextCards)
    this.setState({ saving: true, fetchState: ApiHelper.State.LOADING })
    const res = await CdpHelper.setSetting({
      orgId,
      settingsKey: SETTINGS_KEY,
      settingsValue,
      previousKey: SETTINGS_KEY,
      previousValue,
    })
    if (!res || !res.success) {
      this.setState({ saving: false, fetchState: ApiHelper.State.READY })
      throw new Error((res && res.error) || 'Failed to update funnel')
    }
    const cards = nextCards.map((card, index) => ({
      ...card,
      id: card.key || `card_${index}`,
      order: index + 1,
    }))
    this.setState({
      cards,
      saving: false,
      fetchState: ApiHelper.State.READY,
      currentOrderChangeIndex: null,
      orderChangeEvent: null,
      isOrderChanged: false,
    })
    if (this.props.showToast) {
      this.props.showToast('Funnel updated', 'success')
    }
    return cards
  }

  closeFormModal = () => {
    this.setState({
      modalOpen: false,
      formDefinition: null,
      editingCard: null,
    })
  }

  getCardFormFields = ({ cardType, searchFilters, selectedOperators, editingCard }) => {
    const otherHasValue = (this.state.cards || []).some((c) => {
      if (editingCard && c.id === editingCard.id) return false
      return getCardType(c) === 'VALUE'
    })
    const typeOptions = [{ key: 'FILTER', label: 'CONTACTS COUNT' }]
    if (!otherHasValue) {
      typeOptions.push({ key: 'VALUE', label: 'CONTACTS VALUE' })
    }

    const fields = [
      {
        key: 'label',
        type: 'edit_text',
        label: 'Title',
        tip: 'Display label shown on the stat card',
        placement: 'left',
        v_required: { value: true, err: 'Title is required' },
      },
      {
        key: 'cardType',
        type: 'radio',
        label: 'Type',
        placement: 'left',
        advanced: true,
        options: typeOptions,
        defaultValue: 'FILTER',
        v_required: { value: true, err: 'Type is required' },
      },
    ]

    const filterFields = (this.state.filterOptions || []).map((field, index) => {
      const copy = JSON.parse(JSON.stringify(field))
      if (copy.type === 'radio') {
        copy.type = 'check_box'
      }
      if (copy.v_required && copy.v_required.value === true) {
        copy.v_required = { ...copy.v_required, value: false }
      }
      // Pair with Title on the first row: first filter on the right, then alternate
      copy.placement = index % 2 === 0 ? 'right' : 'left'
      delete copy.fullWidth
      copy.condition = { key: 'cardType', value: 'FILTER' }
      if (selectedOperators && selectedOperators[copy.key]) {
        copy.selectedOperator = selectedOperators[copy.key]
      }
      return copy
    })

    fields.push(...filterFields)

    const values = {
      label: '',
      cardType: cardType || 'FILTER',
      ...(searchFilters || {}),
    }
    return { fields, values }
  }

  openModalForAdd = () => {
    const { fields, values } = this.getCardFormFields({
      cardType: 'FILTER',
    })
    const formDefinition = {
      title: 'Add Stat Card',
      formType: 'filter',
      ctaButtonText: 'Save',
      maxWidth: 'sm',
      steps: [{ fields }],
    }
    Helper.populateFormDefinitionValues(formDefinition, values)
    this.setState({
      modalOpen: true,
      formDefinition,
      editingCard: null,
    })
  }

  openModalForEdit = (current) => {
    const cardType = getCardType(current)
    let searchFilters = {}
    const selectedOperators = {}

    if (cardType === 'FILTER' && current.filter) {
      const filterFormDefConf = buildFilterFormDefConf(this.state.contactFieldDef)
      const getFilterOptionsResponse = CRMHelper.getFilterOptions(
        this.state.contactFieldDef,
        filterFormDefConf,
        `https://filter.slixta.com?${current.filter}`
      )
      const filterOptions = getFilterOptionsResponse.filterOptions || this.state.filterOptions
      const stateField = filterOptions.find((f) => f && f.key === 'state')
      if (stateField) {
        UiHelper.renameLeadStatusFieldOptions(stateField, this.props.orgId)
        UiHelper.removeHiddenCrmContactStates(stateField, this.props.orgId)
      }
      filterOptions.forEach((field) => {
        if (field && field.selectedOperator) {
          selectedOperators[field.key] = field.selectedOperator
        }
      })
      searchFilters = CRMHelper.getNormalizedSearchFilters(
        filterOptions,
        getFilterOptionsResponse.searchFilters || {}
      )
    }

    const { fields, values } = this.getCardFormFields({
      cardType,
      searchFilters,
      selectedOperators,
      editingCard: current,
    })
    values.label = current.label || ''
    values.cardType = cardType

    const formDefinition = {
      title: 'Edit Stat Card',
      formType: 'filter',
      ctaButtonText: 'Save',
      maxWidth: 'sm',
      steps: [{ fields }],
    }
    Helper.populateFormDefinitionValues(formDefinition, values)
    this.setState({
      modalOpen: true,
      formDefinition,
      editingCard: current,
    })
  }

  openModalForDelete = (current) => {
    const formDefinition = {
      formType: 'delete',
      skipDeleteConfirmInput: true,
      title: `Delete ${current.label || current.key || 'stat card'}`,
      ctaButtonText: Helper.getString('delete'),
      steps: [{ fields: [] }],
    }
    this.setState({
      modalOpen: true,
      formDefinition,
      editingCard: current,
    })
  }

  buildFilterQueryFromForm = (formData, filterOptions) => {
    const filterOnlyOptions = (filterOptions || []).filter(
      (f) => f && FILTER_FIELD_KEYS.includes(f.key)
    )
    const updatedSearchFilters = CRMHelper.getUpdatedSearchFilters(filterOnlyOptions, formData || {})
    Object.keys(updatedSearchFilters).forEach((key) => {
      const val = updatedSearchFilters[key]
      if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) {
        delete updatedSearchFilters[key]
      }
    })
    return FilterHelper.processFilterFormSubmission({
      filterFormData: updatedSearchFilters,
      filterQueryData: '',
      timezone: Helper.getLocalTimezone(),
    })
  }

  resolveCardKey = ({ label, cardType, editingCard, cards, filter }) => {
    if (cardType === 'VALUE') {
      return VALUE_KEY
    }
    if (cardType === 'FILTER' && (!filter || String(filter).trim() === '')) {
      return CONTACTS_KEY
    }
    const existingKeys = (cards || [])
      .filter((c) => !editingCard || c.id !== editingCard.id)
      .map((c) => c.key)
    const previousType = editingCard ? getCardType(editingCard) : null
    const previousWasUnfiltered = editingCard && isUnfilteredContactsCard(editingCard)
    // Keep existing key when editing a filtered CONTACTS COUNT card that still has a filter
    if (editingCard && previousType === 'FILTER' && editingCard.key && !previousWasUnfiltered) {
      return editingCard.key
    }
    return generateUniqueKey(label, existingKeys, [VALUE_KEY, CONTACTS_KEY])
  }

  handleCardFormSubmit = async (options, done) => {
    const { formData, filterOptions, caller } = options
    const editingCard = this.state.editingCard
    const label = String((formData && formData.label) || '').trim()
    const cardType = (formData && formData.cardType) || 'FILTER'

    if (!label) {
      caller.setState({ inProcess: false, success: false, message: 'Title is required' })
      return
    }

    const cards = [...(this.state.cards || [])]
    const valueCards = cards.filter((c) => {
      if (editingCard && c.id === editingCard.id) return false
      return getCardType(c) === 'VALUE'
    })
    if (cardType === 'VALUE' && valueCards.length > 0) {
      caller.setState({
        inProcess: false,
        success: false,
        message: 'Only one CONTACTS VALUE stat card is allowed',
      })
      return
    }

    let filter = ''
    if (cardType === 'FILTER') {
      const formFields = (this.state.formDefinition
        && this.state.formDefinition.steps
        && this.state.formDefinition.steps[0]
        && this.state.formDefinition.steps[0].fields) || []
      filter = this.buildFilterQueryFromForm(formData, filterOptions || formFields)
    }

    const isUnfilteredFilterCard = cardType === 'FILTER' && (!filter || String(filter).trim() === '')
    if (isUnfilteredFilterCard) {
      const otherUnfiltered = cards.filter((c) => {
        if (editingCard && c.id === editingCard.id) return false
        return isUnfilteredContactsCard(c)
      })
      if (otherUnfiltered.length > 0) {
        caller.setState({
          inProcess: false,
          success: false,
          message: 'Only one CONTACTS COUNT card without a filter is allowed',
        })
        return
      }
    }

    const key = this.resolveCardKey({ label, cardType, editingCard, cards, filter })
    const nextCard = { key, label, filter: filter || '' }

    let nextCards
    if (editingCard) {
      nextCards = cards.map((c) => (
        c.id === editingCard.id
          ? { ...c, ...nextCard, id: key }
          : c
      ))
    } else {
      nextCards = [...cards, { ...nextCard, id: key }]
    }

    try {
      await this.persistCards(nextCards)
      caller.setState({ inProcess: false, success: true, message: '' })
      this.closeFormModal()
      if (done) done()
    } catch (err) {
      const errMsg = Helper.getErrorMsg(err) || 'Failed to save stat card'
      caller.setState({ inProcess: false, success: false, message: errMsg })
      if (this.props.showToast) {
        this.props.showToast(errMsg, 'error')
      }
    }
  }

  handleDeleteSubmit = async (options, done) => {
    const { caller } = options
    const editingCard = this.state.editingCard
    if (!editingCard) {
      caller.setState({ inProcess: false })
      return
    }
    const nextCards = (this.state.cards || []).filter((c) => c.id !== editingCard.id)
    try {
      await this.persistCards(nextCards)
      caller.setState({ inProcess: false, success: true, message: '' })
      this.closeFormModal()
      if (done) done()
    } catch (err) {
      const errMsg = Helper.getErrorMsg(err) || 'Failed to delete stat card'
      caller.setState({ inProcess: false, success: false, message: errMsg })
      if (this.props.showToast) {
        this.props.showToast(errMsg, 'error')
      }
    }
  }

  customSubmit = (options, done) => {
    const { formType } = options
    if (formType === 'delete' || formType === 'del') {
      return this.handleDeleteSubmit(options, done)
    }
    return this.handleCardFormSubmit(options, done)
  }

  onFilterSubmit = (options, done) => {
    return this.handleCardFormSubmit(options, done)
  }

  orderUp = (current, items, index) => {
    if (index <= 0 || this.state.saving) return
    const next = [...items]
    const tmp = next[index - 1]
    next[index - 1] = next[index]
    next[index] = tmp
    this.setState({
      currentOrderChangeIndex: index,
      orderChangeEvent: 'up',
      isOrderChanged: true,
    })
    this.persistCards(next).catch((err) => {
      const errMsg = Helper.getErrorMsg(err) || 'Failed to reorder'
      this.setState({
        cards: this.loadCardsFromSettings(),
        currentOrderChangeIndex: null,
        orderChangeEvent: null,
        isOrderChanged: false,
        fetchState: ApiHelper.State.READY,
      })
      if (this.props.showToast) {
        this.props.showToast(errMsg, 'error')
      }
    })
  }

  orderDown = (current, items, index) => {
    if (index >= items.length - 1 || this.state.saving) return
    const next = [...items]
    const tmp = next[index + 1]
    next[index + 1] = next[index]
    next[index] = tmp
    this.setState({
      currentOrderChangeIndex: index,
      orderChangeEvent: 'down',
      isOrderChanged: true,
    })
    this.persistCards(next).catch((err) => {
      const errMsg = Helper.getErrorMsg(err) || 'Failed to reorder'
      this.setState({
        cards: this.loadCardsFromSettings(),
        currentOrderChangeIndex: null,
        orderChangeEvent: null,
        isOrderChanged: false,
        fetchState: ApiHelper.State.READY,
      })
      if (this.props.showToast) {
        this.props.showToast(errMsg, 'error')
      }
    })
  }

  getTableHeadersMap = () => ({
    Order: (current) => current.order,
    Title: (current) => (
      <div className={css(Styles.titleCell)}>
        <span className={css(Styles.titleText)}>{current.label || '—'}</span>
        {getCardType(current) === 'VALUE' && (
          <span className={css(Styles.typeBadge)}>{getCardTypeLabel('VALUE')}</span>
        )}
      </div>
    ),
    Filter: (current) => (
      getCardType(current) === 'VALUE'
        ? <span className={css(Styles.emptyFilter)}>—</span>
        : (
          <StatCardFilterChips
            filter={current.filter}
            filterOptions={this.state.filterOptions}
          />
        )
    ),
  })

  onRowActions = (current) => ([
    <Tooltip title='Edit' placement='top-start' key={`${current.id}-edit`}>
      <span className={css(AppStyles.entityTableLinkIcon, Styles.actionIcon)}>
        <i className='material-icons-outlined' onClick={() => this.openModalForEdit(current)}>edit</i>
      </span>
    </Tooltip>,
    <Tooltip title='Delete' placement='top-start' key={`${current.id}-delete`}>
      <span className={css(AppStyles.entityTableLinkIcon, Styles.actionIcon)}>
        <i className='material-icons-outlined' onClick={() => this.openModalForDelete(current)}>delete</i>
      </span>
    </Tooltip>,
  ])

  render() {
    const { open, onClose } = this.props
    const {
      cards,
      loading,
      errMsg,
      modalOpen,
      formDefinition,
    } = this.state

    return (
      <>
        <Dialog
          open={!!open}
          onClose={onClose}
          fullWidth
          maxWidth='md'
          style={{ zIndex: 13000 }}
          classes={{ paper: css(Styles.dialogPaper, AppHelper.isDialogScrollbarEnabled() && AppStyles.dialogScrollbar) }}
        >
          <DialogTitle disableTypography className={css(Styles.dialogTitle)}>
            <Typography variant='h6' className={css(Styles.dialogTitleText)}>
              Lead Funnel
            </Typography>
            <div className={css(Styles.dialogTitleActions)}>
              {!loading && !errMsg && (
                <AppButton
                  onClick={this.openModalForAdd}
                  buttonText='Add Card'
                  buttonStyle={Styles.addButton}
                  buttonTextStyle={Styles.addButtonText}
                />
              )}
              <IconButton aria-label='close' onClick={onClose} className={css(Styles.closeButton)}>
                <CloseIcon />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent className={css(Styles.dialogContent)}>
            {loading && (
              <div className={css(Styles.loading)}>
                <CircularProgress size={28} />
              </div>
            )}
            {!loading && errMsg && (
              <Typography color='error' className={css(Styles.errorText)}>{errMsg}</Typography>
            )}
            {!loading && !errMsg && (
              <TableComponent
                compact
                tableHeadersMap={this.getTableHeadersMap()}
                items={cards}
                fetchState={this.state.fetchState}
                currentOrderChangeIndex={this.state.currentOrderChangeIndex}
                orderChangeEvent={this.state.orderChangeEvent}
                isOrderChanged={this.state.isOrderChanged}
                orderUp={this.orderUp}
                orderDown={this.orderDown}
                orderUIMode={this.state.orderUIMode}
                onActions={this.onRowActions}
              />
            )}
          </DialogContent>
        </Dialog>

        {modalOpen && formDefinition && (
          <DynamicFormDialog
            isOpen={modalOpen}
            onClosed={this.closeFormModal}
            formDefinition={formDefinition}
            customSubmit={this.customSubmit}
            onFilterSubmit={this.onFilterSubmit}
            nestedDialog
            fullWidth
            maxWidth='sm'
          />
        )}
      </>
    )
  }
}

const Styles = StyleSheet.create({
  dialogPaper: {
    minHeight: 360,
    borderRadius: AppTheme.borderRadius,
    '@media (max-width: 767px)': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      width: '100%',
      bottom: 0,
      position: 'fixed',
      margin: 0,
      marginTop: 0,
      maxHeight: 'calc(100% - 140px)',
    },
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 8,
  },
  dialogTitleText: {
    fontWeight: 600,
    fontSize: 22,
  },
  dialogTitleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  closeButton: {
    marginLeft: 4,
  },
  addButton: {
    minWidth: 72,
    height: 36,
    padding: '0 16px',
  },
  addButtonText: {
    fontSize: 14,
    fontWeight: 600,
  },
  dialogContent: {
    paddingTop: 0,
    paddingBottom: 24,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 40,
  },
  errorText: {
    padding: '16px 0',
  },
  actionIcon: {
    marginRight: 4,
  },
  titleCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
  },
  titleText: {
    fontSize: 14,
    color: '#222222',
  },
  typeBadge: {
    fontSize: 11,
    color: CommonTheme.subTextColor,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    maxWidth: 480,
  },
  chipRoot: {
    height: 'auto',
    minHeight: 32,
    padding: 8,
    backgroundColor: AppTheme.secondaryColor,
  },
  chipContainer: {
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    maxWidth: '100%',
  },
  chipTextValue: {
    fontWeight: 600,
  },
  emptyFilter: {
    color: '#999999',
  },
  rawFilter: {
    fontSize: 12,
    color: '#666666',
    wordBreak: 'break-all',
  },
})
